/**
 * Widget-level error boundary.
 *
 * Wraps the high-risk Web3 surfaces (swap widget, RPC status indicator,
 * receipt modal) so a runtime exception degrades to a small inline notice
 * instead of blanking the page. Every catch is forwarded to monitoring
 * (Sentry when present, structured console fallback otherwise).
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { captureError } from "@/lib/monitoring";

type Props = {
  surface: string;
  children: ReactNode;
  /** Render nothing instead of the inline notice (e.g. for overlays). */
  silent?: boolean;
  fallback?: ReactNode;
};

type State = { hasError: boolean };

export class WidgetErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureError(error, {
      surface: this.props.surface,
      componentStack: info.componentStack ?? undefined,
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    if (this.props.silent) return null;
    return (
      <div
        role="alert"
        data-testid={`widget-error-${this.props.surface}`}
        className="mt-3 flex items-start gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs leading-relaxed text-muted-foreground"
      >
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-saffron" aria-hidden />
        <span className="min-w-0">
          This module is temporarily unavailable. Everything else on the page keeps working —
          please refresh to retry.
        </span>
      </div>
    );
  }
}
